import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { request } from '../utils/request';

const OrderSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [isVerifying, setIsVerifying] = useState(true);
  const [verificationResult, setVerificationResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  
  const orderId = searchParams.get('order_id');
  const orderNumber = searchParams.get('order_number');
  const sessionId = searchParams.get('session_id');
  const paypalOrderId = searchParams.get('paypal_order_id');
  const token = searchParams.get('token');
  const payerId = searchParams.get('PayerID');

  useEffect(() => {
    if (sessionId) {
      verifyPaymentSession();
    } else if (paypalOrderId) {
      verifyPayPalPayment();
    } else if (token && payerId && orderId) {
      // PayPal 返回的 token 和 PayerID，需要通过 orderId 获取 PayPal 订单 ID
      verifyPayPalPaymentByOrderId();
    } else {
      setIsVerifying(false);
    }
  }, [sessionId, paypalOrderId, token, payerId, orderId]);

  const verifyPaymentSession = async () => {
    try {
      const response: any = await request({
        url: 'orders/verify-session',
        method: 'POST',
        data: {
          sessionId: sessionId
        }
      });

      if (response.success) {
        setVerificationResult(response.result);
      } else {
        setError(response.message || 'Payment verification failed');
      }
    } catch (error: any) {
      console.error('Payment verification error:', error);
      setError('Failed to verify payment. Please contact support.');
    } finally {
      setIsVerifying(false);
    }
  };

  const verifyPayPalPayment = async () => {
    try {
      const response: any = await request({
        url: 'orders/verify-payment',
        method: 'POST',
        data: {
          orderId: paypalOrderId
        }
      });

      if (response.success) {
        setVerificationResult(response.result);
      } else {
        setError(response.message || 'PayPal payment verification failed');
      }
    } catch (error: any) {
      console.error('PayPal payment verification error:', error);
      setError('Failed to verify PayPal payment. Please contact support.');
    } finally {
      setIsVerifying(false);
    }
  };

  const verifyPayPalPaymentByOrderId = async () => {
    try {
      const response: any = await request({
        url: 'orders/verify-payment-by-order',
        method: 'POST',
        data: {
          orderId: orderId,
          token: token,
          payerId: payerId
        }
      });

      if (response.success) {
        setVerificationResult(response.result);
      } else {
        setError(response.message || 'PayPal payment verification failed');
      }
    } catch (error: any) {
      console.error('PayPal payment verification error:', error);
      setError('Failed to verify PayPal payment. Please contact support.');
    } finally {
      setIsVerifying(false);
    }
  };

  // Loading state
  if (isVerifying) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Verifying Payment...</h2>
          <p className="text-gray-600">Please wait while we confirm your payment.</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
          <div className="text-red-500 text-6xl mb-4">
            <ExclamationTriangleIcon className="w-16 h-16 mx-auto" />
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Payment Verification Failed</h2>
          
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-700 text-sm">{error}</p>
          </div>

          <div className="space-y-3">
            <p className="text-gray-600">
              We couldn't verify your payment. Please contact our support team if you believe this is an error.
            </p>
          </div>

          <div className="mt-6 space-y-3">
            <Link
              to="/orders"
              className="block w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors"
            >
              View My Orders
            </Link>
            
            <Link
              to="/products"
              className="block w-full bg-gray-300 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-400 transition-colors"
            >
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  const order = verificationResult?.order;
  const session = verificationResult?.session;
  const paypalOrder = verificationResult?.paypalOrder;
  const finalOrderNumber = order?.orderNumber || orderNumber;
  const finalOrderId = order?.id || orderId;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
        <div className="text-green-500 text-6xl mb-4">
          <CheckCircleIcon className="w-16 h-16 mx-auto" />
        </div>
        
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Order Confirmed!</h2>
        
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <p className="text-sm text-gray-600 mb-1">Order Number</p>
          <p className="text-lg font-semibold text-gray-900">#{finalOrderNumber}</p>
          {finalOrderId && (
            <>
              <p className="text-sm text-gray-600 mb-1 mt-2">Order ID</p>
              <p className="text-sm text-gray-700">{finalOrderId}</p>
            </>
          )}
          {session && (
            <>
              <p className="text-sm text-gray-600 mb-1 mt-2">Payment Amount</p>
              <p className="text-sm text-gray-700">
                ${(session.amount_total / 100).toFixed(2)} {session.currency?.toUpperCase()}
              </p>
            </>
          )}
          {paypalOrder && (
            <>
              <p className="text-sm text-gray-600 mb-1 mt-2">Payment Amount</p>
              <p className="text-sm text-gray-700">
                ${paypalOrder.amount?.value} {paypalOrder.amount?.currency_code}
              </p>
            </>
          )}
        </div>

        <div className="space-y-3">
          <p className="text-gray-600">
            Thank you for your purchase! We've sent a confirmation email to your registered email address.
          </p>
          
          <p className="text-sm text-gray-500">
            You can track your order status in your account dashboard.
          </p>
        </div>

        <div className="mt-6 space-y-3">
          <Link
            to="/orders"
            className="block w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors"
          >
            View My Orders
          </Link>
          
          <Link
            to="/products"
            className="block w-full bg-gray-300 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-400 transition-colors"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccessPage;
